import { Link, createFileRoute } from '@tanstack/react-router';
import {
  Ban,
  Bell,
  BellRing,
  Bot,
  Brain,
  Building2,
  CalendarCheck,
  CalendarX,
  ChevronRight,
  ClipboardList,
  CreditCard,
  Images,
  type LucideIcon,
  MapPin,
  MessageSquare,
  Palette,
  Plug,
  ShieldCheck,
  Star,
  Wallet,
} from 'lucide-react';

import { PageShell } from '@/components/app/page-shell';
import {
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { useActiveOrganization } from '@/features/organization';
import { ROUTES } from '@/lib/route-paths';

/**
 * Organisation settings — the hub the sidebar's pinned row lands on.
 *
 * Until now that row went to `/dashboard/settings`, which is the PERSONAL
 * profile page (name, password), so "Organisation settings"
 * opened your own account. The org-level settings themselves existed only as
 * routes, reachable from the mobile account list or by typing the URL; on
 * desktop nothing indexed them. This page is that index: one card per
 * destination, grouped the way the account list groups them.
 *
 * Personal settings deliberately do NOT appear here — they hang off the user
 * menu at the foot of the sidebar (NavUser → Settings / Account).
 */
export const Route = createFileRoute('/_authed/dashboard/organisation')({
  component: OrganisationSettingsPage,
});

interface SettingsCard {
  title: string;
  description: string;
  icon: LucideIcon;
  url: string;
  requiresBorradhBookings?: boolean;
}

interface SettingsGroup {
  label: string;
  cards: SettingsCard[];
}

const GROUPS: SettingsGroup[] = [
  {
    label: 'Business',
    cards: [
      {
        title: 'Locations',
        description:
          'Every branch you operate. Team, services and stock are assigned per location.',
        icon: MapPin,
        url: ROUTES.locations,
      },
      {
        title: 'Details',
        description:
          'Business name, website and privacy policy — the details clients see.',
        icon: Building2,
        url: ROUTES.settingsDetails,
      },
      {
        title: 'Privacy',
        description: 'Data requests and workspace deletion.',
        icon: ShieldCheck,
        url: ROUTES.settingsConsentPrivacy,
      },
      {
        title: 'Brand style',
        description:
          'Logo, colours and fonts used across your content and booking page.',
        icon: Palette,
        url: ROUTES.brand,
      },
      {
        title: 'Examples',
        description:
          'Finished posts your carousels copy for style, logo and layout. One is used per generation.',
        icon: Images,
        url: ROUTES.examples,
      },
    ],
  },
  {
    label: 'Bookings',
    cards: [
      {
        title: 'Booking settings',
        description: 'How clients book with you and pay a deposit.',
        icon: CalendarCheck,
        url: ROUTES.settingsBookings,
      },
      {
        title: 'Cancellation and rescheduling',
        description:
          'Online changes, notice periods and deposit refunds for cancellations and no-shows.',
        icon: CalendarX,
        url: ROUTES.settingsCancellations,
        requiresBorradhBookings: true,
      },
      {
        title: 'Reminders',
        description:
          'Email and text reminders sent to clients before their appointment.',
        icon: BellRing,
        url: ROUTES.settingsReminders,
      },
      {
        title: 'Post-treatment reviews',
        description:
          'Set the Google review page to share after a completed treatment.',
        icon: Star,
        url: ROUTES.settingsPostTreatmentReviews,
      },
      {
        title: 'Consent forms',
        description:
          'Forms attached to services and collected before an appointment.',
        icon: ClipboardList,
        url: ROUTES.settingsConsentForms,
      },
      {
        title: 'Intake forms',
        description: 'What you ask new clients before their first visit.',
        icon: ClipboardList,
        url: ROUTES.intakeForms,
      },
      {
        title: 'Blocked time types',
        description:
          'The reasons your team can block out time in the calendar.',
        icon: CalendarX,
        url: ROUTES.settingsBlockedTimeTypes,
      },
    ],
  },
  {
    label: 'Claire & integrations',
    cards: [
      {
        title: 'Integrations',
        description:
          'Manage Facebook, Instagram, WhatsApp, & calendar connections.',
        icon: Plug,
        url: ROUTES.integrations,
      },
      {
        title: 'Claire memories',
        description:
          'What Claire remembers about your business, and what she applies to everything she writes.',
        icon: Brain,
        url: ROUTES.settingsClaireMemories,
      },
      {
        title: 'Claire WhatsApp',
        description: 'The WhatsApp number Claire answers on, and its pairing.',
        icon: Bot,
        url: ROUTES.settingsClaireWhatsapp,
      },
      {
        title: 'Claire ad campaigns',
        description:
          'Ad campaigns Claire leaves to your team instead of replying.',
        icon: Ban,
        url: ROUTES.settingsClaireCampaigns,
      },
      {
        title: 'Message templates',
        description:
          'The wording of the confirmations and reminders you send clients.',
        icon: MessageSquare,
        url: ROUTES.settingsMessageTemplates,
      },
      {
        title: 'Notifications',
        description: 'Which events email or notify you and your team.',
        icon: Bell,
        url: ROUTES.settingsNotifications,
      },
    ],
  },
  {
    label: 'Billing',
    cards: [
      {
        title: 'Payments',
        description:
          'Taking money from clients — card processing, terminals and payouts.',
        icon: Wallet,
        url: ROUTES.settingsPayments,
      },
      {
        title: 'Billing',
        description: 'Your Borrah subscription, invoices and payment method.',
        icon: CreditCard,
        url: ROUTES.settingsBilling,
      },
    ],
  },
];

function OrganisationSettingsPage() {
  const { data: organization } = useActiveOrganization();

  return (
    <PageShell className="pt-10 pb-8" maxWidth="max-w-[960px]">
      <title>Organisation settings | Borrah</title>

      <div>
        <h1 className="font-semibold text-2xl">Organisation settings</h1>
        <p className="text-muted-foreground text-sm">
          Everything that applies to your whole business. Settings for a single
          branch live under that branch&apos;s Location Settings.
        </p>
      </div>

      {GROUPS.map((group) => (
        <section className="flex flex-col gap-3" key={group.label}>
          <h2 className="font-medium text-muted-foreground text-xs uppercase tracking-wide">
            {group.label}
          </h2>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {group.cards
              .filter(
                (card) =>
                  !card.requiresBorradhBookings ||
                  organization?.bookingDestination === 'borradh'
              )
              .map((card) => (
                <SettingsCardLink card={card} key={card.url} />
              ))}
          </div>
        </section>
      ))}
    </PageShell>
  );
}

function SettingsCardLink({ card }: { card: SettingsCard }) {
  return (
    <Link className="group block h-full" to={card.url}>
      <Card className="h-full transition group-hover:border-primary/40 group-hover:bg-accent/40">
        <CardHeader>
          <div className="flex items-start gap-3">
            <span className="bg-muted text-muted-foreground rounded-md p-2">
              <card.icon className="size-4" />
            </span>
            <div className="min-w-0 flex-1">
              <CardTitle className="flex items-center gap-1">
                {card.title}
                <ChevronRight className="text-muted-foreground size-4 shrink-0 opacity-0 transition group-hover:opacity-100" />
              </CardTitle>
              <CardDescription className="mt-1">
                {card.description}
              </CardDescription>
            </div>
          </div>
        </CardHeader>
      </Card>
    </Link>
  );
}
